import { useEffect, useRef, useState } from 'react'
import { Play, Clock } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Button, Modal, Select } from './ui/SharedUI'

const SHIFT_TYPES = [
  { value: 'direct_care', label: 'Direct care' },
  { value: 'no_show_covered', label: 'Covered no-show' },
  { value: 'training', label: 'Training' },
  { value: 'waiting_period', label: 'Waiting period' },
]

// Returns the LOCAL calendar date as YYYY-MM-DD, not the UTC date.
// new Date().toISOString().split('T')[0] returns the UTC day, which differs
// from the local day every evening in US time zones — that mismatch is what
// recorded a shift on the wrong calendar date.
function localDateString(d = new Date()) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatClockTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/**
 * Shared clock-in flow for both the dashboard and the shifts page.
 *
 * Mount this component only while a clock-in is in progress. On mount it either
 * resumes an existing unconfirmed draft (existingDraft) or writes a new draft
 * shift row immediately, anchored to initiatedAt.
 *
 * The clock-in time is frozen the instant the button is pressed (initiatedAt)
 * and is never derived from when the caregiver finishes this form. That frozen
 * value is written to both clock_in and initiated_at. Confirming records
 * confirmed_at; the gap between the two is preserved as evidence rather than
 * hidden. initiated_at is the immutable machine anchor — the UI never edits it.
 *
 * Cancelling, or dismissing the modal, deletes the draft so no orphan open
 * shift is left behind. A draft left by a crash or navigation is picked up
 * again the next time the page mounts (see the resume effect on each page).
 *
 * Note: capture of location (location_lat / location_lng / location_accuracy_m)
 * is intentionally NOT wired here yet. The columns exist so the RLS audit sees
 * them; capture stays off until that audit clears.
 */
export function ClockInModal({
  user,
  recipients = [],
  defaultShiftType = 'direct_care',
  initiatedAt,
  existingDraft = null,
  onClose,
  onToast,
}) {
  const isResume = !!existingDraft
  const anchor = existingDraft?.clock_in || initiatedAt

  const [draftId, setDraftId] = useState(existingDraft?.id || null)
  const [recipientId, setRecipientId] = useState(
    existingDraft?.recipient_id || (recipients.length === 1 ? recipients[0].id : '')
  )
  const [shiftType, setShiftType] = useState(existingDraft?.shift_type || defaultShiftType)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const draftIdRef = useRef(existingDraft?.id || null)
  const creatingRef = useRef(false)
  const mountedRef = useRef(true)

  // Create (or adopt) exactly one draft shift row, on mount.
  useEffect(() => {
    mountedRef.current = true

    if (isResume) {
      draftIdRef.current = existingDraft.id
      setDraftId(existingDraft.id)
      return () => {
        mountedRef.current = false
      }
    }

    // Guard against a second insert (React 18 StrictMode double-invokes effects).
    if (draftIdRef.current || creatingRef.current) {
      return () => {
        mountedRef.current = false
      }
    }
    creatingRef.current = true

    ;(async () => {
      try {
        const { data, error: err } = await supabase
          .from('shifts')
          .insert({
            caregiver_id: user.id,
            recipient_id: recipients.length === 1 ? recipients[0].id : null,
            date: localDateString(new Date(anchor)),
            clock_in: anchor,
            initiated_at: anchor,
            shift_type: defaultShiftType,
            is_active: true,
            tasks: [],
          })
          .select()
          .single()
        if (err) throw err
        draftIdRef.current = data.id
        if (mountedRef.current) setDraftId(data.id)
      } catch (err) {
        if (mountedRef.current) setError(err.message)
      }
    })()

    return () => {
      mountedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Live elapsed since the frozen anchor.
  useEffect(() => {
    if (!anchor) return
    const tick = () => setElapsed(Date.now() - new Date(anchor).getTime())
    tick()
    const i = setInterval(tick, 1000)
    return () => clearInterval(i)
  }, [anchor])

  async function handleConfirm() {
    if (!recipientId) {
      setError('Select a care recipient to confirm the clock-in.')
      return
    }
    if (!draftIdRef.current) {
      setError('Still saving the clock-in — try again in a moment.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { error: err } = await supabase
        .from('shifts')
        .update({
          recipient_id: recipientId,
          shift_type: shiftType,
          confirmed_at: new Date().toISOString(),
        })
        .eq('id', draftIdRef.current)
      if (err) throw err
      onToast?.({ message: 'Clocked in', type: 'success' })
      onClose?.(true)
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  async function handleDiscard() {
    setBusy(true)
    try {
      if (draftIdRef.current) {
        await supabase.from('shifts').delete().eq('id', draftIdRef.current)
      }
    } catch (err) {
      onToast?.({ message: err.message, type: 'error' })
    } finally {
      onClose?.(true)
    }
  }

  const recipientOptions = [
    { value: '', label: recipients.length ? 'Select recipient…' : 'No recipients yet' },
    ...recipients.map(r => ({ value: r.id, label: r.name })),
  ]

  return (
    <Modal open onClose={handleDiscard} title={isResume ? 'Finish clock-in' : 'Clock in'} width={460}>
      {/* Frozen clock-in time + live elapsed */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          marginBottom: '16px',
          background: '#F7FAFA',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
        }}
      >
        <div>
          <div
            style={{
              fontSize: '12px',
              color: '#718096',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '2px',
            }}
          >
            Clock-in time
          </div>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1A202C',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {anchor ? formatClockTime(anchor) : '—'}
          </div>
          <div style={{ fontSize: '12px', color: '#A0AEC0', marginTop: '2px' }}>
            Recorded the moment you pressed clock in.
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', color: '#718096', marginBottom: '2px' }}>Elapsed</div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '18px',
              fontWeight: 500,
              color: '#2C7A7B',
            }}
          >
            {formatElapsed(elapsed)}
          </div>
        </div>
      </div>

      <Select
        label="Care recipient"
        id="clockin_recipient"
        value={recipientId}
        onChange={e => {
          setRecipientId(e.target.value)
          setError(null)
        }}
        options={recipientOptions}
      />

      <Select
        label="Shift type"
        id="clockin_shift_type"
        value={shiftType}
        onChange={e => setShiftType(e.target.value)}
        options={SHIFT_TYPES}
      />

      {/* Dormant placeholder — reserves layout for corroboration.
          Off until the RLS audit clears; wired to nothing. */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#A0AEC0', marginBottom: '4px' }}>
          Confirmer
        </div>
        <div
          style={{
            padding: '9px 11px',
            border: '1px dashed #E2E8F0',
            borderRadius: '8px',
            background: '#FAFAFA',
            color: '#A0AEC0',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Clock size={13} /> Available after the security review
        </div>
      </div>

      {error && (
        <div
          style={{
            fontSize: '13px',
            color: '#C05621',
            background: '#FFFAF0',
            border: '1px solid #FEEBC8',
            borderRadius: '6px',
            padding: '8px 10px',
            marginBottom: '12px',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={handleDiscard} disabled={busy}>
          {isResume ? 'Discard' : 'Cancel'}
        </Button>
        <Button onClick={handleConfirm} disabled={busy || !draftId}>
          <Play size={16} /> Clock in
        </Button>
      </div>
    </Modal>
  )
}
